import React from "react";
import { RoleBasedRoute } from "components/Auth/RoleBasedRoute";
import * as FLAGS from "utils/feature-flags";
import { selectFeatureFlag } from "redux/modules/lenndFlags";
import NoPermission from "components/Platform/NoPermission";
import { canViewCacheManagement } from "redux/modules/permissions/user-permission-profile/selectors";
import { connect } from "react-redux";
import { eventId } from "redux/modules/event/selectors";
import { orgId } from "redux/modules/organization/selectors";
import { user } from "redux/modules/user/selectors";

/**
 * Generic flag-based route wrapper
 * @param {Function} getComponent - Function that returns a Promise resolving to a React component
 * @param {Array|Object} flags - Required flags
 * @param {Object} options - Additional options (requireAll, redirectPath)
 */
export const withFlags = (getComponent, flags, options = {}) => {
  return location =>
    getComponent(location).then(RouteComponent => {
      return props => (
        <RoleBasedRoute
          component={RouteComponent}
          requiredFlags={Array.isArray(flags) ? flags : [flags]}
          requireAll={options.requireAll}
          redirectPath={options.redirectPath}
          {...props}
        />
      );
    });
};

// Predefined flag-based route wrappers for backward compatibility
export const superAdminOnly = getComponent =>
  withFlags(getComponent, FLAGS.IS_SUPER_ADMIN.NAME);

export const adminOnly = getComponent =>
  withFlags(getComponent, [
    FLAGS.IS_LENND_ADMIN.NAME,
    FLAGS.IS_SUPER_ADMIN.NAME
  ]);

export const customerSuccessOnly = getComponent =>
  withFlags(getComponent, FLAGS.IS_CUSTOMER_SUCCESS.NAME);

// New wrapper for superAdmin OR permission check
export const hasSuperAdminOrCachePermission = (
  getComponent,
  moduleId,
  action
) => {
  return location =>
    getComponent(location).then(RouteComponent => {
      return connect(state => ({
        isSuperAdmin: selectFeatureFlag(FLAGS.IS_SUPER_ADMIN.NAME)(state),
        hasPermission: canViewCacheManagement(
          state,
          eventId(state) || orgId(state),
          user(state).id
        )(`${moduleId}_${action}`)
      }))(({ isSuperAdmin, hasPermission, ...props }) => {
        if (!isSuperAdmin && !hasPermission) {
          return <NoPermission returnPath="/home" />;
        }
        return <RouteComponent {...props} />;
      });
    });
};

//  HOC for components that need more access checks in future
export const withAdvancedFeatures = (
  getComponent,
  additionalFlags = [],
  options = {}
) =>
  withFlags(getComponent, [FLAGS.IS_SUPER_ADMIN.NAME, ...additionalFlags], {
    requireAll: true,
    ...options
  });
